/**
 * Returns `true` if the value is not `undefined` or `null`.
 *
 * @param v the value to evaluate
 * @returns `true` if `v` is not `undefined` and not `null`, `false` otherwise.
 * @see notSet
 */
export function hasValue<T>(v: T | null | undefined): v is T {
    return v !== undefined && v !== null;
}

/**
 * Returns `true` if the value is `undefined` of `null`.
 *
 * @param value the value to evaluate
 * @returns `true` if `value` is `undefined` or `null`, `false` otherwise
 * @see hasValue
 */
export function notSet(value: unknown): value is null | undefined {
    return value === undefined || value === null;
}

/**
 * Converts a provided value (typically from data attributes on elements) to
 * a `boolean` value. The result will always be a `boolean` value.
 *
 * For strings to be converted to `true`, they must have a value of
 * `"true"` any other value will always return `false`.
 *
 * Other than for strings, values are coerced using JavaScript truthiness.
 *
 * @param value The value to convert to a `boolean`
 * @param defaultValue (optional) `boolean`
 * value to use as the default if value is `null` or `undefined`.
 * @returns The converted `boolean` value
 */
export function toBoolean(value: unknown, defaultValue?: boolean): boolean {
    if (!hasValue(value) && typeof defaultValue === 'boolean') {
        return defaultValue;
    }

    // Handle string values specifically so that we compare to "true"
    if (typeof value === "string") {
        return value.toLowerCase() === "true";
    }

    // Use truthiness to coerce otherwise and ensure we return a boolean value
    return !!value;
}

export function toNumber(value: unknown, defaultValue: number): number;
export function toNumber(value: unknown): number | undefined;
/**
 * Converts the provided value to a number, returning `defaultValue`
 * if `value` is not set or cannot be converted to a number.
 *
 * @param value The value to convert to a number
 * @param defaultValue (optional) The default value to return if `value` is not set
 * @returns A number from `value` or from `defaultValue`
 */
export function toNumber(value: unknown, defaultValue?: number): number | undefined {
    if (!hasValue(value) && typeof defaultValue === 'number') {
        return defaultValue;
    }
    const n = safeToNumber(value);
    if (notSet(n) && typeof defaultValue === 'number') {
        return defaultValue;
    }
    return n;
}

/**
 * Converts a string or number to a number without causing an empty string,
 * null or undefined from being converted to 0.
 *
 * This is largely intended for processing values from data attributes on HTML
 * elements and not necessarily user input.
 *
 * @param v Value to be converted into a number
 * @returns Either the converted value as a number, or undefined.
 */
export function safeToNumber(v: unknown): number | undefined {
    if (v === undefined || v === null) {
        return undefined;
    }
    if (typeof v === "number") {
        return v as number;
    }
    if (typeof v !== "string") {
        return undefined;
    }
    const trimmedValue = v.toString().trim();
    if (trimmedValue === "") {
        return undefined;
    }
    const vn = Number(trimmedValue);
    if (Number.isNaN(vn)) {
        return undefined;
    }
    return vn;
}

/**
 * Converts an object (typically an `Element` or `jQuery` object) to an array of entries.
 *
 * The intended use of this function is for allowing passing either a `jQuery` object
 * (with one or more elements), an array of `Element` objects, or a single `Element`, and allowing
 * the user of this function to always get an array of `Element` objects, so it can
 * process them uniformly and without using `jQuery`.
 *
 * @param objs The object to convert to an array
 * @returns An array of objects based on the input value.
 */
export function toArray(objs: unknown): unknown[] | HTMLElement[] {
    // If not set, return an empty array so the caller can safely assume an array is always returned
    if (notSet(objs)) {
        return [];
    }
    // If it is already an array, just return the object itself
    if (Array.isArray(objs)) {
        return objs;
    }
    // Convert objects with a toArray function (like jQuery objects)
    // to a regular array of elements
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    if (typeof (objs as any).toArray === "function") {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        return (objs as any).toArray();
    }
    // If this is a single Element, build an array with just that element
    if (objs instanceof HTMLElement) {
        return [objs];
    }
    // Convert any "iterable" objects to an array
    if (typeof objs[Symbol.iterator as keyof object] === 'function') {
        return Array.from(objs as Iterable<unknown>);
    }
    // Otherwise, just build a single entry array for this object
    return [objs];
}

/**
 * Determines whether a value can be parsed as a finite number.
 *
 * Values are evaluated using `parseFloat`, so numeric strings with a
 * parseable prefix are considered numeric.
 *
 * @param n The value to evaluate.
 * @returns `true` if `n` parses to a finite number; otherwise `false`.
 */
export function isNumeric(n: unknown) {
    // @ts-expect-error The actual interface for parseFloat accepts any type,
    //  so we'll ignore the typing error here to maintain backwards compatibility
    //  with anything calling this method
    const v = Number.parseFloat(n);
    return !Number.isNaN(v) && Number.isFinite(v);
}

/**
 * Provided to supply a suitable comparator for Array.sort() to compare numbers.
 * @param a First value to compare.
 * @param b Second value to compare.
 * @returns &lt; 0 if a &lt; b, 0 if a = b, &gt; if a &gt; b
 */
export function compareNumbers(a: number, b: number): number {
    return a - b;
}

/**
 * Returns `true` when the value is a non-null object.
 *
 * This is intentionally broad and includes arrays, dates, and other object
 * instances; it is suitable when callers only need to know whether property
 * access via object keys is possible.
 *
 * @param val The value to evaluate.
 * @returns `true` when `val` is an object and not `null`; otherwise `false`.
 */
export function isRecord(val: unknown): val is Record<string, unknown> {
    return val !== null && typeof val === "object";
}

/**
 * Normalizes an unknown value to an "optional boolean" shape.
 *
 * Accepts only `boolean` and `undefined` as valid inputs, and returns `null`
 * as a sentinel for invalid values.
 *
 * @param val The value to normalize.
 * @returns `true`, `false`, or `undefined` for valid inputs; `null` for invalid inputs.
 */
export function toOptionalBoolean(val: unknown): boolean | undefined | null {
    if (val === undefined || typeof val === "boolean") {
        return val as boolean | undefined;
    }
    return null;
}

/**
 * Determines whether an object defines its own callable `toString` property.
 *
 * Note that inherited `toString` implementations, including the one from
 * `Object.prototype`, are not considered custom implementations.
 *
 * @param value The value to evaluate.
 * @returns `true` if `value` is a non-null object with an own `toString`
 * function; otherwise `false`.
 */
export function hasCustomToString(value: unknown): boolean {
    // 1. Ensure the value is an object and set (not null or undefined)
    if (typeof value !== 'object' || notSet(value)) {
        return false;
    }

    // 2. Check if 'toString' is an owned property and is a function
    return (
        Object.hasOwn(value, 'toString') &&
        typeof (value as { toString?: unknown }).toString === 'function'
    );
}
